import type { ReactNode } from 'react';
import styled from 'styled-components';

import type { TotadminPermission } from 'data/__generated__';
import { useMissingPermissionsReason, usePermissionGate } from 'hooks/useHasPermission';
import { PermissionLockIcon } from 'components/PermissionLock';

/**
 * Le verrou d'un sous-bloc qui n'est ni une section ni un bouton (un tableau de prix dans une section,
 * un encart). Sans les droits, le contenu n'est PAS monté — ses requêtes ne partent pas — et on affiche
 * ce qui manque. Pour une section entière, utiliser `requiredPermissions` sur `SectionContainer`.
 */
export function PermissionGate({
    requiredPermissions,
    children,
}: {
    requiredPermissions: readonly TotadminPermission[];
    children: ReactNode;
}) {
    const { isAllowed, missing } = usePermissionGate(requiredPermissions);
    const reason = useMissingPermissionsReason(missing);

    if (isAllowed) {
        return <>{children}</>;
    }

    return (
        <Locked data-test="permission-gate-locked">
            <PermissionLockIcon className="size-4" />
            {reason}
        </Locked>
    );
}

const Locked = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    font-size: 14px;
    color: ${({ theme }) => theme.infoTextColor};
`;
